/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { ChevronDown, Calendar } from 'lucide-react';

const MidCta = () => {
    useEffect(() => {
        const metrics = document.querySelectorAll('.metric-val');

        function updateMetrics() {
            metrics.forEach(el => {
                const base = parseFloat(el.getAttribute('data-base'));
                const isPercent = el.innerText.includes('%');
                const isK = el.innerText.includes('K') && !el.innerText.includes('%');
                const isCurrency = el.innerText.includes('$');

                let variation = (Math.random() - 0.5) * (base * 0.02);
                let newVal = base + variation;

                if (isPercent) {
                    el.innerText = newVal.toFixed(1) + '%';
                } else if (isK) {
                    el.innerText = newVal.toFixed(3) + 'K';
                } else if (isCurrency) {
                    el.innerText = '$' + newVal.toFixed(2) + 'M';
                } else {
                    el.innerText = Math.round(newVal).toLocaleString();
                }
            });
        }

        const intervalId = setInterval(updateMetrics, 3000);
        return () => clearInterval(intervalId);
    }, []);

    const topMetrics = [
        { label: 'Total Claims', val: '34.558K', base: 34.558 },
        { label: '60-Day Claims', val: '85.6%', base: 85.6 },
        { label: 'Total Denials', val: '618', base: 618 },
        { label: 'Denial Rate %', val: '1.8%', base: 1.8 },
        { label: 'Total Adjudicated', val: '32K', base: 32 },
        { label: 'Zero-Pay Claims', val: '2K', base: 2 },
        { label: 'Zero-Pay Rate %', val: '6.7%', base: 6.7 },
    ];

    const reasons = [
        { l: 'Coordination', w: '95%', v: '$0.77M' },
        { l: 'Patient Resp', w: '80%', v: '$0.47M' },
        { l: 'Timeliness', w: '40%', v: '$0.23M' },
    ];

    return (
        <section className="section-padding-lg bg-primary-light">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-primary rounded-[2rem] flex flex-col md:flex-row items-stretch justify-between overflow-hidden relative md:min-h-[400px]">

                    <div className="p-10 md:p-14 lg:p-16 w-full md:w-[45%] lg:w-[40%] flex flex-col justify-center relative z-10 shrink-0" data-aos="fade-right">
                        <h2 className="heading-lg mb-5">
                            Take a quick tour of Trustpoint's platform
                        </h2>
                        <p className="paragraph-lg mb-8 max-w-sm">
                            See how our all-in-one RCM platform helps you save time, reduce denials, and grow your practice.
                        </p>
                        <div>
                            <a href="#contact" className="btn-primary-lg">
                                Take a tour
                            </a>
                        </div>
                    </div>

                    <div className="w-full md:w-[60%] relative flex items-stretch justify-end" data-aos="fade-left">
                        <div className="bg-primary-light/40 absolute top-8 md:top-12 bottom-0 right-0 left-8 md:left-4 rounded-tl-3xl border-t border-l border-primary-light overflow-hidden flex items-stretch">
                            <div className="w-full bg-primary-light rounded-tl-2xl border-t border-l border-gray-100 overflow-hidden flex flex-col">
                                <div className="h-10 bg-secondary w-full flex items-center px-4 flex-shrink-0">
                                    <div className="flex gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full bg-primary-light/20"></div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-primary-light/20"></div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-primary-light/20"></div>
                                    </div>
                                </div>

                                <div className="flex-1 p-3 flex flex-col gap-2 h-full overflow-hidden text-[#2D3142]">
                                    <div className="flex items-stretch gap-1.5 h-11 shrink-0">
                                        <div className="flex-1 grid grid-cols-7 gap-1.5">
                                            {topMetrics.map((m, i) => (
                                                <div key={i} className="bg-primary-light rounded-lg p-1 border border-gray-100 flex flex-col justify-center items-center text-center">
                                                    <span className="text-[6px] font-bold text-gray-400 uppercase tracking-tight truncate w-full mb-0">{m.label}</span>
                                                    <span className="text-[10px] font-extrabold text-[#2D3142] metric-val" data-base={m.base}>{m.val}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="w-32 bg-primary-light rounded-lg border border-gray-100 flex flex-col overflow-hidden">
                                            <div className="bg-[#3D4B7A] text-white text-[6px] font-bold py-1 px-2 flex justify-between items-center">
                                                <span>Date Range</span>
                                                <ChevronDown className="w-2 h-2" />
                                            </div>
                                            <div className="flex-1 flex items-center px-1.5 justify-between">
                                                <span className="text-[5.5px] text-gray-500 font-semibold truncate">6/1/25 - 12/31/25</span>
                                                <Calendar className="w-2 h-2 text-gray-400 shrink-0" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 gap-2 h-36 shrink-0">
                                        <div className="col-span-4 bg-primary-light rounded-xl border border-gray-100 p-2.5 flex flex-col overflow-hidden">
                                            <span className="text-[7px] font-bold text-gray-800 mb-2 border-b border-gray-50 pb-1">60-Day Yield %</span>
                                            <div className="flex-1 relative">
                                                <div className="absolute inset-0 text-[5px] text-[#3D4B7A] font-extrabold pointer-events-none z-10">
                                                    <span className="absolute top-[35%] left-[5%]">77.6%</span>
                                                    <span className="absolute top-[3%] left-[75%]">92.7%</span>
                                                </div>
                                                <svg className="w-full h-full overflow-visible" viewBox="0 0 100 60" preserveAspectRatio="none">
                                                    <polyline fill="none" stroke="#3D4B7A" strokeWidth="1.2" strokeLinecap="round" points="5,45 20,35 35,32 55,20 75,10 95,25" />
                                                </svg>
                                                <div className="absolute inset-0 pointer-events-none">
                                                    <div className="absolute w-1 h-1 bg-primary-light border border-[#3D4B7A] rounded-full" style={{ left: '75%', top: '16%', transform: 'translate(-50%, -50%)' }}></div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between text-[5px] text-gray-400 font-bold mt-1 pt-1 border-t border-gray-50">
                                                <span>Jun 2025</span><span>Dec 2025</span>
                                            </div>
                                        </div>

                                        <div className="col-span-4 bg-primary-light rounded-xl border border-gray-100 p-2.5 flex flex-col overflow-hidden">
                                            <span className="text-[7px] font-bold text-gray-800 mb-2 border-b border-gray-50 pb-1">Denial Rate Trends</span>
                                            <div className="flex-1 relative">
                                                <svg className="w-full h-full overflow-visible" viewBox="0 0 100 60" preserveAspectRatio="none">
                                                    <path d="M5,10 L25,40 L45,45 L65,40 L85,10 L95,45" fill="none" stroke="#263056" strokeWidth="1.2" strokeLinecap="round" />
                                                </svg>
                                                <div className="absolute inset-0 pointer-events-none">
                                                    <div className="absolute w-1 h-1 bg-primary-light border border-[#263056] rounded-full" style={{ left: '5%', top: '16%', transform: 'translate(-50%, -50%)' }}></div>
                                                    <div className="absolute w-1 h-1 bg-primary-light border border-[#263056] rounded-full" style={{ left: '85%', top: '16%', transform: 'translate(-50%, -50%)' }}></div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between text-[5px] text-gray-400 font-bold mt-1 pt-1 border-t border-gray-50">
                                                <span>Jun 2025</span><span>Dec 2025</span>
                                            </div>
                                        </div>

                                        <div className="col-span-4 bg-primary-light rounded-xl border border-gray-100 p-2.5 flex flex-col overflow-hidden">
                                            <span className="text-[7px] font-bold text-gray-800 mb-2 border-b border-gray-50 pb-1">Denial Reasons</span>
                                            <div className="space-y-2">
                                                {reasons.map((r, i) => (
                                                    <div key={i} className="flex flex-col gap-0.5">
                                                        <div className="flex justify-between text-[5px] font-bold uppercase">
                                                            <span className="text-gray-400">{r.l}</span>
                                                            <span className="text-gray-900">{r.v}</span>
                                                        </div>
                                                        <div className="h-1 bg-gray-50 rounded-full overflow-hidden">
                                                            <div className="h-full bg-[#3D4B7A] rounded-full" style={{ width: r.w }}></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 gap-2 h-32 shrink-0">
                                        <div className="col-span-4 bg-primary-light rounded-xl border border-gray-100 p-2.5 flex flex-col overflow-hidden">
                                            <span className="text-[7px] font-bold text-gray-800 mb-2 border-b border-gray-50 pb-1">Zero-Pay Rate</span>
                                            <div className="flex-1 relative">
                                                <svg className="w-full h-full overflow-visible" viewBox="0 0 100 60" preserveAspectRatio="none">
                                                    <path d="M5,40 L20,35 L40,15 L60,10 L80,25 L95,45" fill="none" stroke="#263056" strokeWidth="1.2" strokeLinecap="round" />
                                                </svg>
                                            </div>
                                        </div>

                                        <div className="col-span-8 bg-primary-light rounded-xl border border-gray-100 p-2.5 flex flex-col overflow-hidden">
                                            <span className="text-[7px] font-bold text-gray-800 mb-1.5">Carrier Breakdown</span>
                                            <div className="flex-1 grid grid-cols-6 gap-1" id="carrier-grid">
                                                <div className="col-span-3 bg-[#3D4B7A] rounded-lg p-1.5 text-white text-[6px] font-bold flex items-center justify-center transition-all duration-1000 ease-in-out">BCBS MN</div>
                                                <div className="col-span-2 bg-[#263056] rounded-lg p-1.5 text-white text-[6px] font-bold flex items-center justify-center">MEDICARE</div>
                                                <div className="col-span-1 bg-gray-50 rounded-lg flex items-center justify-center">
                                                    <span className="text-[7.5px] font-bold text-accent metric-val" data-base="109">109</span>
                                                </div>
                                                <div className="col-span-2 bg-[#77AEC5] rounded-lg flex items-center justify-center text-white text-[6px] font-bold">182</div>
                                                <div className="col-span-4 grid grid-cols-4 gap-1">
                                                    <div className="bg-accent/10 rounded-lg"></div>
                                                    <div className="bg-secondary/10 rounded-lg"></div>
                                                    <div className="bg-accent/10 rounded-lg"></div>
                                                    <div className="bg-gray-100 rounded-lg"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes pulse-gentle { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.02); opacity: 0.95; } }
                .pulse-slow { animation: pulse-gentle 4s infinite ease-in-out; }
                .pulse-slower { animation: pulse-gentle 6s infinite ease-in-out; }
            `}</style>
        </section>
    );
};

export default MidCta;
